import { useCallback, useEffect, useState } from "react";
import { SortColumn } from "react-data-grid";

import { CompleteBlueIcon } from "../../../../../assets";
import { ProjectPhase } from "../../../../../constants/project-phase";
import { TaskConstants } from "../../../../../constants/workflow/TaskConstants";
import { CursorChangeProps, ResultType, TaskPanelData } from "../../../../../models";
import { GetTaskBoardResponse, searchWorkflowTasks, SearchWorkflowTasksResponse } from "../../../../../service/query";
import { getTaskBoard } from "../../../../../service/query/QueryService.full";
import { ResultData, Status } from "../../../../../service/Shared";
import { flattenObject } from "../../../../../utils";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  DataGridCursorDataLoadEventHandler,
} from "../../../../../widget";
import { AvatarCellFormatter } from "../../../../../widget/data/DataGrid/components/CellFormatters";
import { IconCellFormatterData } from "../../../../../widget/data/DataGrid/models";

interface UseDealUpcomingTasksReturnData {
  columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<unknown>)[];
  defaultSortingCriteria: SortColumn[];
  refreshTasks: boolean;
  handleRefresh: () => void;
  handleOpenCreateTaskPanel: () => void;
  isUpcomingTasksLoading: boolean;
  upcomingTasksOnChange: DataGridCursorDataLoadEventHandler;
  workflow: GetTaskBoardResponse["results"][number] | undefined;
  createTaskStepUuid: string | undefined;
  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;
  showWorkflowTaskPanel: boolean;
  handleCloseWorkflowTaskPanel: () => void;
  selectedCompleteTask: TaskPanelData | undefined;
  showCompleteTaskModal: boolean;
  handleCloseCompleteTaskModal: () => void;
}

const defaultSortingCriteria: SortColumn[] = [{ columnKey: "deadlineDate", direction: "ASC" }];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
  {
    name: "Task",
    key: "taskLink",
    dataType: "string",
    formatter: "buttonLink",
    alignment: "left",
    filterable: false,
    minWidth: 280,
    sortable: false,
  },
  {
    name: "Assigned to",
    key: "avatar",
    dataType: "string",
    formatter: "custom",
    alignment: "center",
    customCellFormatter: AvatarCellFormatter,
    filterable: false,
    minWidth: 140,
    sortable: false,
  },
  {
    name: "Status",
    key: "status",
    dataType: "string",
    formatter: "stringWithEllipsis",
    alignment: "left",
    filterable: false,
    minWidth: 140,
    sortable: false,
  },
  {
    key: "deadlineDate",
    name: "Deadline",
    dataType: "Date",
    formatter: "dateOnlyWithIndicator",
    alignment: "left",
    filterable: false,
    minWidth: 160,
    sortable: false,
  },
  {
    name: "Complete",
    key: "completeIcon",
    dataType: "boolean",
    formatter: "icon",
    alignment: "center",
    filterable: false,
    sortable: false,
    minWidth: 104,
  },
];

export const useDealUpcomingTasks = (workflowUuid?: string | null): UseDealUpcomingTasksReturnData => {
  const [workflow, setWorkflow] = useState<GetTaskBoardResponse["results"][number]>();
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();
  const [selectedCompleteTask, setSelectedCompleteTask] = useState<TaskPanelData>();
  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);
  const [showCompleteTaskModal, setShowCompleteTaskModal] = useState(false);
  const [isUpcomingTasksLoading, setIsUpcomingTasksLoading] = useState(true);
  const [refreshTasks, setRefreshTasks] = useState(false);

  const handleRefresh = useCallback((): void => {
    setRefreshTasks((prev) => !prev);
  }, []);

  const handleCloseWorkflowTaskPanel = useCallback((): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  }, []);

  const handleOpenCreateTaskPanel = useCallback((): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(true);
  }, []);

  const handleCloseCompleteTaskModal = useCallback((): void => {
    setSelectedCompleteTask(undefined);
    setShowCompleteTaskModal(false);
  }, []);

  const formatUpcomingTasksData = useCallback(
    (responseData: SearchWorkflowTasksResponse | undefined): ResultData[] =>
      responseData?.results?.map((task) => {
        const row = flattenObject(task) as ResultData & { avatar: { fullName?: string } };

        row.avatar = { fullName: task.assignee?.name };
        row.taskLink = {
          text: task.name,
          action: () => {
            setSelectedPanelTask(task);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        if (task.status !== TaskConstants.TASK_STATUS_UNASSIGNED) {
          row.completeIcon = {
            action: () => {
              setSelectedCompleteTask({
                deadlineDate: task.deadlineDate,
                isMandatory: task.isMandatory,
                isMilestone: task.isMilestone,
                keyDate: task.keyDate,
                status: task.status,
                repeat: task.repeat,
                description: task.description,
                name: task.name,
                visibility: [],
                assignedToUserUuid: task.assignee?.uuid ?? null,
                assignedToUserName: task.assignee?.name ?? null,
                ownerOrganisationUuid: task.ownerOrganisation.uuid,
                relateToActivity: task.objectType === "Activity",
                activityUuid: task.objectType === "Activity" ? task.objectUuid : null,
                stepUuid: task.step.uuid,
                phase: task.step.phase as ProjectPhase,
                uuid: task.uuid,
                rowVersion: task.rowVersion,
                resolution: task.resolution,
              });
              setShowCompleteTaskModal(true);
            },
            icon: <CompleteBlueIcon width={20} height={20} strokeWidth={1.5} />,
          } as IconCellFormatterData;
        }

        return row;
      }) || [],
    []
  );

  const upcomingTasksOnChange: DataGridCursorDataLoadEventHandler = async ({ paging }: CursorChangeProps) => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: paging.pageSize || 5,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    if (!workflowUuid) {
      setIsUpcomingTasksLoading(false);
      return data;
    }

    setIsUpcomingTasksLoading(true);

    await searchWorkflowTasks({
      paging: {
        limit: 5,
        beforeCursor: null,
        afterCursor: null,
      },
      sort: [{ key: "results.deadlineDate", direction: "asc" }],
      filter: {
        results: {
          status: {
            operator: "neq",
            value: TaskConstants.TASK_STATUS_COMPLETE,
          },
          workflowUuid: {
            operator: "eq",
            value: workflowUuid,
          },
        },
      },
    })
      .then((response) => {
        data = {
          resultData: formatUpcomingTasksData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            hasNextPage: false,
            hasPreviousPage: false,
            pageSize: paging.pageSize || 5,
            totalCount: response.data?.paging?.total || response.data?.results?.length || 0,
          },
        };
      })
      .finally(() => {
        setIsUpcomingTasksLoading(false);
      });

    return data;
  };

  useEffect(() => {
    if (!workflowUuid) {
      setWorkflow(undefined);
      return;
    }

    const fetchWorkflow = async (): Promise<void> => {
      const response = await getTaskBoard({
        showComplete: true,
        paging: {
          afterCursor: null,
          beforeCursor: null,
          limit: 1,
        },
        filter: {
          results: {
            uuid: {
              operator: "eq",
              value: workflowUuid,
            },
          },
        },
      });

      if (response.status === Status.Success && response.data) {
        setWorkflow(response.data.results[0]);
      }
    };

    fetchWorkflow();
  }, [workflowUuid, refreshTasks]);

  const createTaskStepUuid = workflow?.steps.find((step) => !step.isComplete)?.uuid ?? workflow?.steps[0]?.uuid;

  return {
    columns,
    defaultSortingCriteria,
    refreshTasks,
    handleRefresh,
    handleOpenCreateTaskPanel,
    isUpcomingTasksLoading,
    upcomingTasksOnChange,
    workflow,
    createTaskStepUuid,
    selectedPanelTask,
    showWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    selectedCompleteTask,
    showCompleteTaskModal,
    handleCloseCompleteTaskModal,
  };
};
